import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment, Paper, Pagination, Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, useTheme
} from "@mui/material";
import { Helmet } from "react-helmet";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PublicIcon from "@mui/icons-material/Public";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import { useUser } from "../context/UserContext";
import DOMPurify from 'dompurify';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // import styles
import PageLoader from "../components/PageLoader";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import ConfirmDialog from "../components/ConfirmDialog";
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import DailyDevotionSettingsModal from "../components/DailyDevotionSettingsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveManager from "../utils/archiveManager";
import ViewUserActivityModal from "../components/ViewUserActivityModal";
import TimelineIcon from "@mui/icons-material/Timeline";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ModuleAnalyticsDashboard from "../components/ModuleAnalyticsDashboard";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import MobileDailyDevotionModal from "../components/MobileDailyDevotionModal";
// Function to strip HTML and decode HTML entities
const stripHtml = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = DOMPurify.sanitize(html); // prevents XSS
  return tempDiv.textContent || tempDiv.innerText || '';
};

// Helper to get devotion category/section name
// Kumara Krupa is the default daily devotion source/feed
export const getDevotionSection = (devotion, fallbackOrgName = null) => {
  if (devotion?.category && devotion.category.trim()) {
    return devotion.category.trim();
  }
  if (devotion?.section_name && devotion.section_name.trim()) {
    return devotion.section_name.trim();
  }
  if (devotion?.origanisation?.org_name) {
    return devotion.origanisation.org_name;
  }
  if (devotion?.origanisation?.title) {
    return devotion.origanisation.title;
  }
  if (devotion?.org_name && devotion.org_id && Number(devotion.org_id) > 1) {
    return devotion.org_name;
  }
  if (Number(devotion?.org_id) === 61 || (devotion?.author && devotion.author.trim() === 'Raj')) {
    return 'Ananth Jeevan';
  }
  if (fallbackOrgName && Number(devotion?.org_id) > 1) {
    return fallbackOrgName;
  }
  return 'Kumara Krupa';
};


const DailyDevotions = () => {
  const theme = useTheme();
  const isLight = theme?.palette?.mode === 'light';
  const { user, isSwitched, orgViewMode, effectiveOrgId: contextEffectiveOrgId } = useUser();
  const isSwitchedOrgMode = Boolean(
    isSwitched ||
    orgViewMode ||
    contextEffectiveOrgId ||
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token') ||
    sessionStorage.getItem('gpn_org_view_mode') ||
    localStorage.getItem('gpn_org_view_mode')
  );
  const showOrganizationColumn = Boolean(user?.role === 1 && !isSwitchedOrgMode && !user?.org_id);
  const canModifyDevotion = (devotion) => Boolean(user?.role === 1 || !devotion?.org_id || Number(user?.org_id) === Number(devotion?.org_id));
  const [activeViewMode, setActiveViewMode] = useState('content'); // 'content' vs 'analytics'
  const [value, setValue] = useState("<p>Hello, world!</p>");

  const [search, setSearch] = useState("");
  const [devotionType, setDevotionType] = useState(true);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [organization, setOrganization] = useState(null); // eslint-disable-line

  // Active organization ID resolved from context, view mode, or user
  const activeOrgId = useMemo(() => {
    if (contextEffectiveOrgId) return contextEffectiveOrgId;
    if (orgViewMode?.orgId) return orgViewMode.orgId;
    try {
      const stored = sessionStorage.getItem('gpn_org_view_mode');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.orgId) return parsed.orgId;
      }
    } catch { }
    if (organization && organization.id !== 0) return organization.id;
    if (user?.org_id && user?.org_id !== 1) return user.org_id;
    return null;
  }, [contextEffectiveOrgId, orgViewMode, organization, user]);
  const [devotionList, setDevotionList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // eslint-disable-line
  const [editUser, setEditUser] = useState(null);
  const [users, setUsers] = useState([]); // eslint-disable-line
  const [openDialog, setOpenDialog] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [devotionSettingsModalOpen, setDevotionSettingsModalOpen] = useState(false);
  const [mobilePreviewModal, setMobilePreviewModal] = useState({ open: false, isExample: true, devotion: null });

  // Derive default category name from organization name
  const defaultCategoryName = useMemo(() => {
    let localOrgName = null;
    try {
      const storedOrgView = sessionStorage.getItem("gpn_org_view_mode");
      if (storedOrgView) {
        const parsed = JSON.parse(storedOrgView);
        if (parsed?.orgName) localOrgName = parsed.orgName;
      }
      if (!localOrgName) {
        const storedUser = localStorage.getItem("userInfo");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          localOrgName = (
            parsed?.organization ||
            parsed?.org_name ||
            parsed?.origanisation?.org_name ||
            parsed?.origanisation?.title ||
            parsed?.organization_name ||
            parsed?.organizations?.name ||
            null
          );
        }
      }
    } catch { }

    const resolvedOrg = (
      orgViewMode?.orgName ||
      localOrgName ||
      (organization && (organization.title || organization.name || organization.org_name)) ||
      user?.organization ||
      user?.org_name ||
      user?.origanisation?.org_name ||
      user?.origanisation?.title ||
      user?.organization_name ||
      user?.organizations?.name ||
      (user?.role === 1 ? 'Glocal Prayer Network' : null)
    );

    return (resolvedOrg && resolvedOrg.trim()) ? resolvedOrg.trim() : 'Kumara Krupa';
  }, [orgViewMode, organization, user]);

  // Super Admin in GPN organization check:
  // When Super Admin is in GPN (global scope or Org 1), they have the option to post only in GPN or share globally
  const isSuperAdminInGPN = useMemo(() => {
    if (user?.role !== 1) return false;
    if (activeOrgId && Number(activeOrgId) > 1) return false;
    return true;
  }, [user, activeOrgId]);

  // "Post only in this organization" option state (default is false => shared with all organizations)
  const [postOnlyThisOrg, setPostOnlyThisOrg] = useState(false);
  const [editPostOnlyThisOrg, setEditPostOnlyThisOrg] = useState(false);

  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);

  // Section/category grouping across the entire dataset (Kumara Krupa + custom user sections)
  const [activeSourceTab, setActiveSourceTab] = useState('All');
  const [allKnownSections, setAllKnownSections] = useState(['Kumara Krupa']);
  const [allCatalogDevotions, setAllCatalogDevotions] = useState([]);
  const [isNewDevotionMode, setIsNewDevotionMode] = useState(false);
  const [allViewMode, setAllViewMode] = useState('cards'); // 'cards' | 'table'
  const [editIsNewCategory, setEditIsNewCategory] = useState(false);

  const [PostDate, setPostDate] = useState('');
  const [Title, setTitle] = useState('');
  const [Dailydeovotion, setDailydeovotion] = useState('');
  const [Quote, setQuote] = useState('');
  const [Author, setAuthor] = useState('');
  const [Category, setCategory] = useState(defaultCategoryName);
  const [VideoUrl, setVideoUrl] = useState('');
  const [scheduleType, setScheduleType] = useState('fixed'); // 'fixed' | 'floating'
  const [durationDays, setDurationDays] = useState(15);
  const [userProgressModalOpen, setUserProgressModalOpen] = useState(false);

  // Keep Category synced with default organization category name
  useEffect(() => {
    if (defaultCategoryName) {
      setCategory(prev => (!prev || prev === 'Kumara Krupa') ? defaultCategoryName : prev);
    }
  }, [defaultCategoryName]);

  useEffect(() => {
    if (defaultCategoryName && defaultCategoryName !== 'Kumara Krupa') {
      setAllKnownSections(prev => (
        prev.includes(defaultCategoryName) ? prev : [...prev, defaultCategoryName]
      ));
    }
  }, [defaultCategoryName]);

  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('daily-devotion'));
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('daily-devotion'));
  const [filterActiveTab, setFilterActiveTab] = useState('all');

  const [selectedDevotionForView, setSelectedDevotionForView] = useState(null);
  const [openDetailViewDialog, setOpenDetailViewDialog] = useState(false);
  const [userActivityModalOpen, setUserActivityModalOpen] = useState(false);

  const handleOpenDevotionDetail = (devotion) => {
    setSelectedDevotionForView(devotion);
    setOpenDetailViewDialog(true);
  };

  const handleCloseDevotionDetail = () => {
    setOpenDetailViewDialog(false);
    setSelectedDevotionForView(null);
  };

  const API_URL = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(true);

  // Auto-save draft on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (Title.trim() || Dailydeovotion.trim() || VideoUrl.trim()) {
        DraftManager.saveDraft('daily-devotion', {
          PostDate, Title, Dailydeovotion, Quote, Author, VideoUrl
        }, activeDraftId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [PostDate, Title, Dailydeovotion, Quote, Author, VideoUrl, activeDraftId]);

  const handleManualSaveDraft = () => {
    if (!Title.trim() && !Dailydeovotion.trim() && !VideoUrl.trim()) {
      setToast({ open: true, message: "Please enter a title, devotion text, or video URL before saving draft.", severity: "warning" });
      return;
    }
    const res = DraftManager.saveDraft('daily-devotion', {
      PostDate, Title, Dailydeovotion, Quote, Author, VideoUrl
    }, activeDraftId);

    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Daily devotion draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    const d = draft.data;
    if (d.PostDate !== undefined) setPostDate(d.PostDate);
    if (d.Title !== undefined) setTitle(d.Title);
    if (d.Dailydeovotion !== undefined) setDailydeovotion(d.Dailydeovotion);
    if (d.Quote !== undefined) setQuote(d.Quote);
    if (d.Author !== undefined) setAuthor(d.Author);
    if (d.VideoUrl !== undefined) setVideoUrl(d.VideoUrl);
    setActiveDraftId(draft.id);
    setOpenDialog(true);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('daily-devotion', draftId);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };

  const handleArchive = (devotion) => {
    setConfirmDialog({
      open: true,
      title: "Archive Daily Devotion",
      message: "Are you sure you want to archive this devotion? It will no longer be visible to mobile app users until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('daily-devotion', devotion);
        setArchivedList(ArchiveManager.getArchivedItems('daily-devotion'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (openDetailViewDialog) handleCloseDevotionDetail();
        setToast({ open: true, message: "Devotion archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (devotionId) => {
    ArchiveManager.restoreItem('daily-devotion', devotionId);
    setArchivedList(ArchiveManager.getArchivedItems('daily-devotion'));
    setToast({ open: true, message: "Daily devotion restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (devotionId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete",
      message: "Are you sure you want to permanently delete this archived devotion? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('daily-devotion', devotionId);
        setArchivedList(ArchiveManager.getArchivedItems('daily-devotion'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (openDetailViewDialog) handleCloseDevotionDetail();
        setToast({ open: true, message: "Devotion permanently removed.", severity: "success" });
      }
    });
  };

  // Modal open/close
  const handleDialogOpen = (presetCategory = null, isNew = false) => {
    setPostDate('');
    setTitle('');
    setDailydeovotion('');
    setQuote('');
    setAuthor('');
    if (isNew) {
      setIsNewDevotionMode(true);
      setCategory('');
    } else {
      setIsNewDevotionMode(false);
      const targetCategory = presetCategory || (activeSourceTab !== 'All' ? activeSourceTab : (defaultCategoryName || 'Kumara Krupa'));
      setCategory(targetCategory);
    }
    setVideoUrl('');
    setScheduleType('fixed');
    setDurationDays(15);
    setActiveDraftId(null);
    setImageFile(null);
    setImagePreview(null);
    setPostOnlyThisOrg(false);
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    if (Title.trim() || Dailydeovotion.trim() || VideoUrl.trim()) {
      DraftManager.saveDraft('daily-devotion', {
        PostDate, Title, Dailydeovotion, Quote, Author, Category, VideoUrl, post_only_this_org: postOnlyThisOrg
      }, activeDraftId);
      setDraftsList(DraftManager.getDrafts('daily-devotion'));
    }
    setImageFile(null);
    setImagePreview(null);
    setOpenDialog(false);
  };

  const handleEdit = (id) => {
    const item = devotionList.find((devotion) => devotion.id === id) || allCatalogDevotions.find((devotion) => devotion.id === id);
    if (item) {
      setEditUser({
        ...item,
        video_url: item.video_url || '',
        category: item.category || getDevotionSection(item)
      });
      setEditIsNewCategory(false);
      setEditPostOnlyThisOrg(Boolean(item.post_only_this_org));
      setEditImageFile(null);
      setEditImagePreview(null);
    }
  };
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const effectiveOrgId = activeOrgId;

        // When browsing 'Kumara Krupa' (the default devotion stream), query global devotions (organization: null)
        const orgQueryParam = activeSourceTab === 'Kumara Krupa' ? null : (effectiveOrgId || null);

        const response = await fetch(`${API_URL}devotion-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: search,
            organization: orgQueryParam,
            page: page,
            fromDate: fromDate,
            toDate: toDate,
            author: activeSourceTab !== 'All' ? activeSourceTab : undefined,
          })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          if (data.message === 'Invalid token' || response.status === 401 || response.status === 403) {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
        }

        if (!response.ok || data.success === false) {
          throw new Error(data.message || 'Could not load daily devotions');
        }

        if (!Array.isArray(data.devotions) || !data.pagination) {
          throw new Error('The daily devotion service returned an invalid response');
        }

        setDevotionList(data.devotions || []);
        setTotalItems(Number(data.pagination?.total) || data.devotions.length);
        setTotalPages(Math.max(1, Number(data.pagination?.totalPages) || 1));

        // Robust settings extraction: handle both array and object responses
        let isDefaultActive = true;
        if (data.settings) {
          const settingVal = Array.isArray(data.settings)
            ? (data.settings.find(s => String(s.org_id) === String(effectiveOrgId)) || data.settings.find(s => !s.org_id || s.org_id === 1) || data.settings[0])
            : data.settings;
          isDefaultActive = settingVal ? settingVal.hdailydevotiondefault !== 'no' : true;
        }
        setDevotionType(isDefaultActive);

        // Accumulate known sections/categories for category tabs
        if (data.allCategories && Array.isArray(data.allCategories) && data.allCategories.length > 0) {
          setAllKnownSections(data.allCategories);
        } else if (data.devotions && data.devotions.length > 0) {
          setAllKnownSections(prev => {
            const newSections = data.devotions
              .map(d => getDevotionSection(d))
              .filter(a => !prev.includes(a));
            return newSections.length > 0 ? Array.from(new Set(['Kumara Krupa', ...prev, ...newSections])).sort() : prev;
          });
        }
      } catch (err) {
        console.error('Error fetching daily devotions:', err);
        setDevotionList([]);
        setTotalItems(0);
        setTotalPages(1);
        setToast({
          open: true,
          message: err?.message || 'Could not load daily devotions. Please try again.',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [fromDate, toDate, organization, activeOrgId, search, page, activeSourceTab, API_URL]);

  // Background fetch of all authors & devotions across all pages for full-dataset category browsing
  useEffect(() => {
    let isMounted = true;
    const fetchFullDataset = async () => {
      try {
        const token = localStorage.getItem('token');
        const effectiveOrgId = activeOrgId;

        // 1. Fetch global / default devotions catalog (organization: null)
        const fetchGlobalPromise = fetch(`${API_URL}devotion-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: '',
            organization: null,
            page: 1,
            perPage: 100,
          })
        }).then(r => r.json()).catch(() => ({}));

        // 2. If viewing a specific organization, also fetch that organization's custom devotions
        const fetchOrgPromise = effectiveOrgId ? fetch(`${API_URL}devotion-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: '',
            organization: effectiveOrgId,
            page: 1,
            perPage: 100,
          })
        }).then(r => r.json()).catch(() => ({})) : Promise.resolve({});

        const [globalData, orgData] = await Promise.all([fetchGlobalPromise, fetchOrgPromise]);
        if (!isMounted) return;

        let allItems = [];
        if (Array.isArray(globalData?.devotions)) {
          allItems.push(...globalData.devotions);
        }
        if (Array.isArray(orgData?.devotions)) {
          allItems.push(...orgData.devotions);
        }

        const totalP = globalData?.pagination?.totalPages || 1;

        // If backend returned less than total items for global catalog, fetch remaining pages
        if (globalData?.devotions && globalData.devotions.length < (globalData.pagination?.total || 0) && totalP > 1) {
          const promises = [];
          for (let p = 2; p <= Math.min(totalP, 10); p++) {
            promises.push(
              fetch(`${API_URL}devotion-list`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ search: '', organization: null, page: p, perPage: 100 })
              }).then(r => r.json()).then(d => d.devotions || []).catch(() => [])
            );
          }
          const restPages = await Promise.all(promises);
          restPages.forEach(batch => {
            if (Array.isArray(batch)) allItems = [...allItems, ...batch];
          });
        }

        if (isMounted) {
          // Deduplicate by ID
          const uniqueMap = new Map();
          allItems.forEach(item => uniqueMap.set(String(item.id), item));
          const uniqueItems = Array.from(uniqueMap.values());
          setAllCatalogDevotions(uniqueItems);

          // Extract all unique sections/categories from the entire database
          const sections = Array.from(
            new Set(['Kumara Krupa', ...uniqueItems.map(d => getDevotionSection(d))])
          ).filter(Boolean).sort();

          if (sections.length > 0) {
            setAllKnownSections(sections);
          }
        }
      } catch (e) {
        console.error('Error fetching full devotions catalog:', e);
      }
    };

    fetchFullDataset();
    return () => { isMounted = false; };
  }, [activeOrgId, API_URL]);

  const handleViewDialogClose = () => {

    setEditUser(null);

  }

  const handleAddDevotion = async () => {
    const token = localStorage.getItem('token');

    if (PostDate == '' || Title == '' || Dailydeovotion == '') {
      setToast({ open: true, message: "Post Date, Title and Daily Devotion are required", severity: "warning" });
      return false;
    }

    if (isNewDevotionMode && (!Category || !Category.trim())) {
      setToast({ open: true, message: "Please enter a Devotion Name for the new devotion series", severity: "warning" });
      return false;
    }

    const resolvedCategory = (Category && Category.trim())
      ? Category.trim()
      : (activeSourceTab !== 'All' ? activeSourceTab : (defaultCategoryName || 'Kumara Krupa'));

    // Use FormData so we can attach an optional image file
    const formData = new FormData();
    formData.append('post_date', PostDate);
    formData.append('title', Title);
    formData.append('post_content', Dailydeovotion);
    formData.append('quote', Quote);
    formData.append('author', Author);
    formData.append('category', resolvedCategory);
    formData.append('video_url', VideoUrl);
    formData.append('post_only_this_org', postOnlyThisOrg ? 'true' : 'false');
    if (imageFile) {
      formData.append('devotion_image', imageFile);
    }

    const response = await fetch(`${API_URL}add-devotion`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Do NOT set Content-Type when sending FormData — browser sets it automatically with boundary
      },
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
    }

    if (response.ok) {
      if (activeDraftId) {
        DraftManager.deleteDraft('daily-devotion', activeDraftId);
      }
      setToast({ open: true, message: "Devotion added successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      setToast({ open: true, message: "Add failed: " + (data?.message || 'Unknown error'), severity: "error" });
    }
  };


  const handleSaveEdit = async () => {
    const token = localStorage.getItem('token');

    if (editUser.post_date == '' || editUser.title == '' || editUser.post_content == '') {
      alert("Post Date and Title and Daily Devotion required")
      return false;
    }

    // Use FormData so we can attach an optional replacement image
    const formData = new FormData();
    formData.append('id', editUser.id);
    formData.append('post_date', editUser.post_date);
    formData.append('title', editUser.title);
    formData.append('post_content', editUser.post_content);
    formData.append('quote', editUser.quote || '');
    formData.append('author', editUser.author || '');
    formData.append('category',
      (editUser.category !== undefined && editUser.category !== null && editUser.category.trim() !== '')
        ? editUser.category.trim()
        : (getDevotionSection(editUser) || defaultCategoryName || 'Kumara Krupa')
    );
    formData.append('video_url', editUser.video_url || '');
    formData.append('post_only_this_org', editPostOnlyThisOrg ? 'true' : 'false');
    if (editImageFile) {
      formData.append('devotion_image', editImageFile);
    }

    const response = await fetch(`${API_URL}update-devotion`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Do NOT set Content-Type when sending FormData
      },
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
      // 🔑 Handle invalid token
      if (data.message === 'Invalid token') {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
    }

    if (response.ok) {
      setToast({ open: true, message: "Devotion updated successfully", severity: "success" });
      setTimeout(() => { window.location.reload(); }, 1500);
    } else {
      setToast({ open: true, message: data?.message || 'Unknown error', severity: "error" });
    }
  };




  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      title: "Delete Daily Devotion",
      message: "Are you sure you want to delete this devotion? This action cannot be undone.",
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        if (openDetailViewDialog) handleCloseDevotionDetail();

        try {
          const token = localStorage.getItem('token');

          const response = await fetch(`${API_URL}delete-devotion`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id }),
          });

          const data = await response.json();

          if (!response.ok || data.success === false) {
            if (data.message === 'Invalid token') {
              localStorage.removeItem('token');
              window.location.href = '/login';
              return;
            }
          }

          if (response.ok) {
            setToast({ open: true, message: "Devotion deleted successfully", severity: "success" });
            setTimeout(() => { window.location.reload(); }, 1500);
          } else {
            setToast({ open: true, message: data?.message || 'Failed to delete devotion', severity: "error" });
          }
        } catch (error) {
          console.error('Error deleting devotion:', error);
          setToast({ open: true, message: 'An unexpected error occurred', severity: "error" });
        }
      }
    });
  };



  // Section tabs derived from clean categories (Kumara Krupa + custom user sections with devotions)
  const sourceTabs = useMemo(() => {
    const set = new Set(['All', 'Kumara Krupa']);
    const catalogToCheck = allCatalogDevotions.length > 0 ? allCatalogDevotions : devotionList;
    catalogToCheck.forEach(d => {
      const sec = getDevotionSection(d, defaultCategoryName);
      if (sec) set.add(sec);
    });
    // Filter out categories that have 0 devotions (except 'All' and 'Kumara Krupa')
    return Array.from(set).filter(tab => {
      if (tab === 'All' || tab === 'Kumara Krupa') return true;
      const cnt = catalogToCheck.filter(d => getDevotionSection(d, defaultCategoryName) === tab).length;
      return cnt > 0;
    });
  }, [defaultCategoryName, allCatalogDevotions, devotionList]);

  // Devotion series / names groups for the 'All' tab overview
  const devotionGroups = useMemo(() => {
    const catalogToCheck = allCatalogDevotions.length > 0 ? allCatalogDevotions : devotionList;
    const activeCatalog = catalogToCheck.filter(
      (d) => !archivedList.some((arch) => String(arch.id) === String(d.id))
    );
    const names = sourceTabs.filter((t) => t !== 'All');

    return names.map((name) => {
      const items = activeCatalog.filter(
        (d) => getDevotionSection(d, defaultCategoryName) === name
      );
      const sorted = [...items].sort(
        (a, b) =>
          new Date(b.post_date || b.date || b.created_at || 0) -
          new Date(a.post_date || a.date || a.created_at || 0)
      );
      return {
        name,
        count: items.length,
        latest: sorted[0] || null,
        items,
      };
    }).filter((g) => g.count > 0 || g.name === 'Kumara Krupa' || g.name === defaultCategoryName);
  }, [sourceTabs, allCatalogDevotions, devotionList, defaultCategoryName, archivedList]);

  // Filtered devotion groups when searching on the 'All' tab
  const displayedDevotionGroups = useMemo(() => {
    if (!search || !search.trim()) return devotionGroups;
    const q = search.trim().toLowerCase();
    return devotionGroups.filter(g =>
      g.name.toLowerCase().includes(q) ||
      (g.latest?.title && g.latest.title.toLowerCase().includes(q))
    );
  }, [devotionGroups, search]);

  // Entire-dataset filtering:
  // When activeSourceTab === 'All', show allCatalogDevotions (or devotionList).
  // When activeSourceTab !== 'All':
  // Filter by section name (Kumara Krupa, or custom user section) across allCatalogDevotions
  const displayedDevotions = useMemo(() => {
    if (filterActiveTab === "archived") {
      return archivedList;
    }

    let list = devotionList;
    if (activeSourceTab !== 'All') {
      const checkList = allCatalogDevotions.length > 0 ? allCatalogDevotions : devotionList;
      list = checkList.filter(d => getDevotionSection(d, defaultCategoryName) === activeSourceTab);
    } else {
      // In 'All' tab:
      if (allCatalogDevotions.length > 0) {
        list = allCatalogDevotions;
      } else if (devotionType && allCatalogDevotions.length > 0) {
        const listIds = new Set(devotionList.map(d => String(d.id)));
        const defaultItems = allCatalogDevotions.filter(d => !d.org_id || (d.org_id === 1 && !d.post_only_this_org));
        const missingDefaults = defaultItems.filter(d => !listIds.has(String(d.id)));
        if (missingDefaults.length > 0) {
          list = [...devotionList, ...missingDefaults].sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0));
        }
      }
    }

    // Apply search filter if searching
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(d => (d.title && d.title.toLowerCase().includes(q)) || (d.author && d.author.toLowerCase().includes(q)));
    }

    // Apply date range filter client-side if dates are chosen
    if (fromDate) {
      const from = new Date(fromDate + 'T00:00:00');
      list = list.filter(d => new Date(d.post_date || d.date || d.created_at) >= from);
    }
    if (toDate) {
      const to = new Date(toDate + 'T23:59:59');
      list = list.filter(d => new Date(d.post_date || d.date || d.created_at) <= to);
    }

    return list.filter((devotion) => !archivedList.some((arch) => String(arch.id) === String(devotion.id)));
  }, [filterActiveTab, archivedList, devotionList, allCatalogDevotions, activeSourceTab, devotionType, search, fromDate, toDate]);

  const effectiveTotalPages = useMemo(() => {
    if (activeSourceTab !== 'All' || displayedDevotions.length > devotionList.length) {
      return Math.max(1, Math.ceil(displayedDevotions.length / 10));
    }
    return Math.max(totalPages, 1);
  }, [totalPages, displayedDevotions.length, devotionList.length, activeSourceTab]);

  const sourceFilteredDevotions = useMemo(() => {
    if (activeSourceTab !== 'All' || displayedDevotions.length > 10) {
      const start = (page - 1) * 10;
      return displayedDevotions.slice(start, start + 10);
    }
    return displayedDevotions;
  }, [displayedDevotions, page, activeSourceTab]);

  return (
    <>    <Helmet>
      <title>Daily Devotions Management - GPN</title>
    </Helmet>

      <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Header with Top-Right Settings Button (Like Prayers) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#FFFFFF',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.65rem' },
                letterSpacing: '-0.3px',
                mb: 0.5
              }}
            >
              Daily Devotions
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.65)',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              Manage Devotional Content, Bible Meditations and Settings
            </Typography>
          </Box>

          {/* Top Right Actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Devotion Analytics Icon Button */}
            <Tooltip title="Daily Devotion Analytics" placement="bottom">
              <IconButton
                onClick={() => setActiveViewMode(activeViewMode === 'analytics' ? 'content' : 'analytics')}
                sx={{
                  color: activeViewMode === "analytics" ? "#FFFFFF" : (isLight ? "#0D9488" : "#64d2e2"),
                  bgcolor: activeViewMode === "analytics"
                    ? (isLight ? "#0D9488" : "#177373")
                    : (isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)"),
                  border: activeViewMode === "analytics"
                    ? "1.5px solid #64d2e2"
                    : (isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)"),
                  borderRadius: "8px",
                  p: 0.85,
                  transition: "all 0.2s ease-in-out",
                  boxShadow: activeViewMode === "analytics" ? "0 0 12px rgba(100, 210, 226, 0.4)" : "none",
                  "&:hover": {
                    bgcolor: activeViewMode === "analytics"
                      ? (isLight ? "#0f766e" : "#146262")
                      : (isLight ? "rgba(13, 148, 136, 0.2)" : "rgba(76, 161, 175, 0.3)"),
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <AnalyticsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Daily Devotion Settings Icon Button */}
            <Tooltip title="Daily Devotion Settings" placement="bottom">
              <IconButton
                onClick={() => setDevotionSettingsModalOpen(true)}
                sx={{
                  color: isLight ? "#0D9488" : "#64d2e2",
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)",
                  border: isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)",
                  borderRadius: "8px",
                  p: 0.85,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: isLight ? "rgba(13, 148, 136, 0.18)" : "rgba(76, 161, 175, 0.3)",
                    borderColor: isLight ? "#0D9488" : "#4CA1AF",
                    transform: "rotate(30deg)",
                  },
                }}
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {activeViewMode === 'analytics' ? (
          <ModuleAnalyticsDashboard
            contentType="devotion"
            moduleTitle="Daily Devotions Engagement & Reads Analytics"
            hideHeader={true}
          />
        ) : (
          <>
            {/* UNIFIED ACTION, CATEGORY & FILTER BAR (Compact 2-Line Layout) */}
            <Paper
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 2.5,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* LINE 1: Title, Status Badge & Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              pb: 1.5,
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            {/* Left: Heading + Devotions Count + Back Button */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.2}>
              {filterActiveTab === "archived" ? (
                <Button
                  startIcon={<ArrowBackIcon />}
                  size="small"
                  onClick={() => { setFilterActiveTab("all"); setActiveSourceTab('All'); }}
                  sx={{
                    color: '#64d2e2',
                    backgroundColor: 'rgba(76, 161, 175, 0.15)',
                    border: '1px solid rgba(76, 161, 175, 0.35)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.4,
                    fontSize: '0.8rem',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 161, 175, 0.3)',
                      borderColor: '#64d2e2',
                    }
                  }}
                >
                  Back to Devotions
                </Button>
              ) : activeSourceTab !== "All" && (
                <Button
                  startIcon={<ArrowBackIcon />}
                  size="small"
                  onClick={() => { setActiveSourceTab("All"); setPage(1); }}
                  sx={{
                    color: '#64d2e2',
                    backgroundColor: 'rgba(76, 161, 175, 0.15)',
                    border: '1px solid rgba(76, 161, 175, 0.35)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.4,
                    fontSize: '0.8rem',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 161, 175, 0.3)',
                      borderColor: '#64d2e2',
                    }
                  }}
                >
                  All Devotion Names
                </Button>
              )}
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                {filterActiveTab === "archived" ? "Archived Devotions" : (activeSourceTab === "All" ? "All Devotions" : activeSourceTab)}
              </Typography>
              <Chip
                size="small"
                label={
                  filterActiveTab === "archived"
                    ? `${displayedDevotions.length} devotions`
                    : activeSourceTab === "All" && allViewMode === "cards"
                    ? `${devotionGroups.length} series • ${allCatalogDevotions.length || devotionList.length} devotions`
                    : `${displayedDevotions.length} ${displayedDevotions.length === 1 ? 'devotion' : 'devotions'}`
                }
                sx={{
                  bgcolor: 'rgba(76, 161, 175, 0.2)',
                  color: '#64d2e2',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24
                }}
              />
            </Box>

            {/* Right: Action Buttons */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              {/* User Example Mobile Screen Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<PhoneIphoneIcon sx={{ fontSize: 16 }} />}
                onClick={() => setMobilePreviewModal({ open: true, isExample: true, devotion: null })}
                sx={{
                  color: isLight ? "#0D9488" : "#64d2e2",
                  borderColor: isLight ? "rgba(13, 148, 136, 0.4)" : "rgba(76, 161, 175, 0.4)",
                  backgroundColor: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(76, 161, 175, 0.12)",
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.8,
                  py: 0.6,
                  fontSize: '0.82rem',
                  '&:hover': {
                    backgroundColor: isLight ? "rgba(13, 148, 136, 0.16)" : "rgba(76, 161, 175, 0.25)",
                    borderColor: isLight ? "#0D9488" : "#64d2e2",
                  }
                }}
              >
                User Example
              </Button>

              {filterActiveTab !== "archived" && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleDialogOpen(activeSourceTab !== 'All' ? activeSourceTab : null, activeSourceTab === 'All')}
                  sx={{
                    background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                    color: '#FFFFFF',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    py: 0.6,
                    fontSize: '0.82rem',
                    boxShadow: '0 4px 12px rgba(23, 115, 115, 0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #1C8A8A 0%, #2f7e8a 100%)' }
                  }}
                >
                  {activeSourceTab === 'All' ? "Add Daily Devotion" : `Add Devotion under ${activeSourceTab}`}
                </Button>
              )}

              <Button
                variant={filterActiveTab === "archived" ? "contained" : "outlined"}
                size="small"
                startIcon={filterActiveTab === "archived" ? <ArrowBackIcon /> : <InventoryIcon />}
                onClick={() => { setFilterActiveTab(filterActiveTab === "archived" ? "all" : "archived"); setActiveSourceTab('All'); }}
                sx={{
                  color: filterActiveTab === "archived" ? '#fff' : '#64d2e2',
                  borderColor: 'rgba(76, 161, 175, 0.4)',
                  backgroundColor: filterActiveTab === "archived" ? '#177373' : 'transparent',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1.8,
                  py: 0.6,
                  fontSize: '0.82rem',
                  '&:hover': {
                    backgroundColor: filterActiveTab === "archived" ? '#135e5e' : 'rgba(76, 161, 175, 0.15)',
                  }
                }}
              >
                {filterActiveTab === "archived" ? "Back to All Devotions" : `Archived (${archivedList.length})`}
              </Button>
            </Box>
          </Box>

          {/* LINE 2: Category Tabs on Left + Search & Dates on Right */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            {/* Category / Source Tabs */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
              {filterActiveTab !== 'archived' && sourceTabs.length > 1 && sourceTabs.map((tab) => {
                const listToCheck = allCatalogDevotions.length > 0 ? allCatalogDevotions : devotionList;
                const count = tab === 'All'
                  ? devotionGroups.length
                  : listToCheck.filter(d => getDevotionSection(d, defaultCategoryName) === tab).length;
                return (
                  <Box
                    key={tab}
                    onClick={() => { setActiveSourceTab(tab); setPage(1); }}
                    sx={{
                      px: 1.5,
                      py: 0.4,
                      borderRadius: 20,
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      userSelect: 'none',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.6,
                      ...(activeSourceTab === tab
                        ? {
                          background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                          color: '#FFFFFF',
                          boxShadow: '0 2px 8px rgba(23, 115, 115, 0.4)',
                        }
                        : {
                          background: 'rgba(255,255,255,0.06)',
                          color: 'rgba(255,255,255,0.6)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          '&:hover': { background: 'rgba(76,161,175,0.15)', color: '#64d2e2' },
                        }),
                    }}
                  >
                    <span>{tab}</span>
                    {count > 0 && (
                      <Box
                        component="span"
                        sx={{
                          px: 0.7,
                          py: 0.1,
                          borderRadius: 10,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          backgroundColor: activeSourceTab === tab ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
                        }}
                      >
                        {count}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Filters: Search & Date Pickers */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.2,
                alignItems: "center",
                width: { xs: "100%", md: "auto" }
              }}
            >
              <TextField
                size="small"
                placeholder="Search by Title..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 180px', lg: '0 0 200px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    color: '#FFFFFF',
                    fontSize: '0.82rem',
                    height: 36,
                  }
                }}
              />

              <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 100%', sm: '0 0 auto' }, width: { xs: '100%', sm: 'auto' } }}>
                <TextField
                  size="small"
                  label="From Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => {
                    if (e.target.value.length <= 10) setFromDate(e.target.value);
                  }}
                  inputProps={{ max: "9999-12-31" }}
                  sx={{
                    flex: { xs: 1, sm: 'none' },
                    width: { sm: 135 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      color: '#FFFFFF',
                      fontSize: '0.82rem',
                      height: 36,
                    }
                  }}
                />

                <TextField
                  size="small"
                  label="To Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => {
                    if (e.target.value.length <= 10) setToDate(e.target.value);
                  }}
                  inputProps={{ max: "9999-12-31" }}
                  sx={{
                    flex: { xs: 1, sm: 'none' },
                    width: { sm: 135 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      color: '#FFFFFF',
                      fontSize: '0.82rem',
                      height: 36,
                    }
                  }}
                />
              </Box>

              {(search || fromDate || toDate) && (
                <Button
                  size="small"
                  onClick={() => { setSearch(''); setFromDate(''); setToDate(''); setPage(1); }}
                  sx={{
                    color: '#F87171',
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    py: 0.5,
                  }}
                >
                  Reset
                </Button>
              )}

              {filterActiveTab !== 'archived' && activeSourceTab === 'All' && (
                <Box display="flex" alignItems="center" gap={0.5} sx={{ bgcolor: 'rgba(255,255,255,0.04)', p: 0.4, borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Tooltip title="View Devotion Names (Cards)">
                    <IconButton
                      size="small"
                      onClick={() => setAllViewMode('cards')}
                      sx={{
                        bgcolor: allViewMode === 'cards' ? '#177373' : 'transparent',
                        color: allViewMode === 'cards' ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
                        borderRadius: 1.5,
                        p: 0.6,
                        '&:hover': { bgcolor: allViewMode === 'cards' ? '#135e5e' : 'rgba(76,161,175,0.15)', color: '#fff' }
                      }}
                    >
                      <ViewModuleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View All Devotions (Table)">
                    <IconButton
                      size="small"
                      onClick={() => setAllViewMode('table')}
                      sx={{
                        bgcolor: allViewMode === 'table' ? '#177373' : 'transparent',
                        color: allViewMode === 'table' ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
                        borderRadius: 1.5,
                        p: 0.6,
                        '&:hover': { bgcolor: allViewMode === 'table' ? '#135e5e' : 'rgba(76,161,175,0.15)', color: '#fff' }
                      }}
                    >
                      <ViewListIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>

        {/* DEVOTIONS TABLE / CARDS VIEW */}
        {loading && <SectionLoader open={loading} />}

        {filterActiveTab !== 'archived' && activeSourceTab === 'All' && allViewMode === 'cards' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2.5,
              mb: 3,
            }}
          >
            {displayedDevotionGroups.map((group) => {
              const latestImg = group.latest?.image_url || group.latest?.image || '';
              const latestDate = group.latest?.post_date || group.latest?.date;
              const formattedDate = latestDate
                ? new Date(latestDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                : null;

              return (
                <Card
                  key={group.name}
                  sx={{
                    borderRadius: '16px',
                    background: isLight ? '#FFFFFF' : 'rgba(21, 34, 46, 0.75)',
                    border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.25)',
                    boxShadow: isLight ? '0 4px 16px rgba(15, 23, 42, 0.05)' : '0 8px 24px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.25s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      borderColor: '#64d2e2',
                      boxShadow: '0 12px 28px rgba(76, 161, 175, 0.2)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header Row: Thumbnail + Devotion Name + Count Badge */}
                    <Box display="flex" alignItems="flex-start" gap={1.8} mb={2}>
                      <Avatar
                        variant="rounded"
                        src={latestImg || undefined}
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: '12px',
                          bgcolor: 'rgba(76, 161, 175, 0.2)',
                          color: '#64D2E2',
                          border: '1px solid rgba(76, 161, 175, 0.3)',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                          flexShrink: 0
                        }}
                      >
                        <MenuBookIcon sx={{ fontSize: 26 }} />
                      </Avatar>
                      <Box flexGrow={1} minWidth={0}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={0.4}>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            noWrap
                            sx={{
                              color: isLight ? '#0F172A' : '#FFFFFF',
                              fontSize: '1.05rem',
                              letterSpacing: '-0.2px'
                            }}
                            title={group.name}
                          >
                            {group.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${group.count} ${group.count === 1 ? 'devotion' : 'devotions'}`}
                            sx={{
                              bgcolor: 'rgba(76, 161, 175, 0.2)',
                              color: '#64d2e2',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              height: 22,
                              borderRadius: '6px'
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.75rem', display: 'block' }}>
                          Devotion Feed / Category
                        </Typography>
                      </Box>
                    </Box>

                    {/* Middle: Latest Devotion Summary */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                        border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.06)',
                        mb: 2,
                        flexGrow: 1
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
                        Latest Message
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          color: isLight ? '#1E293B' : '#E2E8F0',
                          fontSize: '0.86rem',
                          lineHeight: 1.3,
                          mb: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {group.latest?.title || 'No devotions added yet'}
                      </Typography>
                      {formattedDate && (
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <CalendarTodayIcon sx={{ fontSize: 13, color: 'rgba(255, 255, 255, 0.45)' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.73rem' }}>
                            {formattedDate} {group.latest?.author ? `• By ${group.latest.author}` : ''}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Actions: View Devotions + Add Devotion Under this + Mobile Preview */}
                    <Box display="flex" gap={1} mt="auto">
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          setActiveSourceTab(group.name);
                          setPage(1);
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                          color: '#FFFFFF',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          py: 0.7,
                          '&:hover': { background: 'linear-gradient(135deg, #1C8A8A 0%, #2f7e8a 100%)' }
                        }}
                      >
                        View Devotions
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleDialogOpen(group.name, false)}
                        sx={{
                          color: '#64d2e2',
                          borderColor: 'rgba(76, 161, 175, 0.4)',
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          px: 1.5,
                          py: 0.7,
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            borderColor: '#64d2e2',
                            backgroundColor: 'rgba(76, 161, 175, 0.15)'
                          }
                        }}
                      >
                        + Add
                      </Button>
                      <Tooltip title="Preview on Mobile Screen">
                        <IconButton
                          size="small"
                          onClick={() => setMobilePreviewModal({
                            open: true,
                            isExample: false,
                            devotion: group.latest || { category: group.name, title: group.name }
                          })}
                          sx={{
                            color: '#64d2e2',
                            border: '1px solid rgba(76, 161, 175, 0.4)',
                            borderRadius: '8px',
                            px: 1,
                            py: 0.7,
                            '&:hover': {
                              borderColor: '#64d2e2',
                              backgroundColor: 'rgba(76, 161, 175, 0.15)'
                            }
                          }}
                        >
                          <PhoneIphoneIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            {/* Add New Daily Devotion Card */}
            <Card
              onClick={() => handleDialogOpen(null, true)}
              sx={{
                borderRadius: '16px',
                background: 'rgba(21, 34, 46, 0.4)',
                border: '2px dashed rgba(76, 161, 175, 0.4)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                minHeight: 220,
                transition: 'all 0.25s ease',
                '&:hover': {
                  borderColor: '#64d2e2',
                  background: 'rgba(23, 115, 115, 0.15)',
                  transform: 'translateY(-3px)',
                }
              }}
            >
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  bgcolor: 'rgba(76, 161, 175, 0.2)',
                  color: '#64d2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                  boxShadow: '0 4px 14px rgba(76, 161, 175, 0.25)'
                }}
              >
                <AddIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#FFFFFF', fontSize: '1rem', mb: 0.5 }}>
                Add New Daily Devotion
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.78rem', mb: 2, maxWidth: 220 }}>
                Create a new devotion series/category and publish daily messages under it.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  px: 2,
                  pointerEvents: 'none'
                }}
              >
                Create New Devotion
              </Button>
            </Card>
          </Box>
        ) : (
          <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: "16px",
            overflow: "hidden",
            border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.25)",
            background: isLight ? "#FFFFFF" : "rgba(21, 34, 46, 0.7)",
            boxShadow: isLight ? "0 4px 16px rgba(15, 23, 42, 0.05)" : "none",
            mb: 3,
          }}
        >
          {sourceFilteredDevotions.length === 0 && !loading ? (
            <Box py={8} textAlign="center">
              <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                {filterActiveTab === "archived" ? "No archived devotions found" : activeSourceTab !== 'All' ? `No devotions from "${activeSourceTab}"` : "No devotions found"}
              </Typography>
              {filterActiveTab === "archived" && (
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => { setFilterActiveTab("all"); setActiveSourceTab('All'); }}
                  sx={{
                    mt: 2,
                    color: isLight ? "#0F766E" : '#64d2e2',
                    borderColor: isLight ? "rgba(15, 118, 110, 0.4)" : 'rgba(76, 161, 175, 0.4)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.8,
                  }}
                >
                  Back to All Devotions
                </Button>
              )}
            </Box>
          ) : (
            <Table size="small" sx={{ minWidth: 950 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: isLight ? "#F1F5F9" : "#1a2636",
                      color: isLight ? "#1E293B" : "#F8FAFC",
                      fontWeight: 800,
                      fontSize: "0.78rem",
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      borderBottom: isLight ? "2px solid #CBD5E1" : "1.5px solid rgba(255,255,255,0.12)",
                      py: 1.5,
                    }
                  }}
                >
                  <TableCell align="center" sx={{ width: 50 }}>#</TableCell>
                  <TableCell sx={{ width: 90 }}>Banner</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>Devotion Title & Reflection</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Author</TableCell>
                  {showOrganizationColumn && <TableCell sx={{ minWidth: 150 }}>Organization</TableCell>}
                  <TableCell sx={{ minWidth: 120 }}>Scheduled Date</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                  <TableCell align="center" sx={{ width: 170 }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sourceFilteredDevotions.map((devotion, index) => {
                  const imageUrl = devotion.image_url || devotion.image || '';
                  const plainContent = stripHtml(devotion.post_content || '');
                  const plainQuote = devotion.quote ? stripHtml(devotion.quote) : '';
                  const sectionLabel = getDevotionSection(devotion, defaultCategoryName);

                  return (
                    <TableRow
                      key={devotion.id || index}
                      hover
                      onClick={() => handleOpenDevotionDetail(devotion)}
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        '&:hover': {
                          bgcolor: isLight ? "#F0FDFA !important" : "rgba(13, 148, 136, 0.1) !important",
                        },
                        '& td': {
                          borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.06)",
                          py: 1.35,
                          color: isLight ? "#1E293B" : "#F1F5F9",
                        }
                      }}
                    >
                      {/* Index */}
                      <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)", fontSize: "0.82rem" }}>
                        {((page - 1) * 9) + index + 1}
                      </TableCell>

                      {/* Thumbnail */}
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={imageUrl || undefined}
                          sx={{
                            width: 64,
                            height: 44,
                            borderRadius: "8px",
                            bgcolor: isLight ? "rgba(15, 118, 110, 0.1)" : "rgba(76, 161, 175, 0.2)",
                            color: isLight ? "#0F766E" : "#64D2E2",
                            border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255,255,255,0.15)",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                          }}
                        >
                          <MenuBookIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                      </TableCell>

                      {/* Title & Reflection */}
                      <TableCell>
                        <Box sx={{ pr: 1 }}>
                          <Box display="flex" alignItems="center" gap={0.8} mb={0.4} flexWrap="wrap">
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{
                                color: isLight ? "#0D9488" : "#5EEAD4",
                                fontSize: "0.92rem",
                                lineHeight: 1.25,
                              }}
                            >
                              {devotion.title || "Untitled Devotion"}
                            </Typography>
                            <Chip
                              size="small"
                              label={sectionLabel}
                              sx={{
                                height: 20,
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                borderRadius: "6px",
                                bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.08)",
                                color: isLight ? "#0F766E" : "#64d2e2",
                                border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.3)",
                              }}
                            />
                          </Box>
                          {plainQuote && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isLight ? "#D97706" : "#FBBF24",
                                fontStyle: "italic",
                                display: "block",
                                mb: 0.3,
                                fontWeight: 600,
                              }}
                            >
                              "{plainQuote.slice(0, 100)}{plainQuote.length > 100 ? '...' : ''}"
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)",
                              fontSize: "0.82rem",
                              lineHeight: 1.35,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {plainContent.slice(0, 180)}...
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Author */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 600, color: isLight ? "#334155" : "#E2E8F0" }}>
                          {devotion.author ? `By ${devotion.author}` : "Staff / Ministry"}
                        </Typography>
                      </TableCell>

                      {/* Organization */}
                      {showOrganizationColumn && (
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ fontSize: "0.82rem", color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)" }}>
                            {devotion.origanisation?.org_name || devotion.org_name || (devotion.org_id && Number(devotion.org_id) > 1 ? `Org #${devotion.org_id}` : "Global (GPN)")}
                          </Typography>
                        </TableCell>
                      )}

                      {/* Scheduled Date */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                          {devotion.post_date || (devotion.created_at ? new Date(devotion.created_at).toLocaleDateString() : "N/A")}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={devotion.is_archived || filterActiveTab === "archived" ? "Archived" : "Active"}
                          sx={{
                            height: 22,
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            textTransform: "uppercase",
                            bgcolor: devotion.is_archived || filterActiveTab === "archived" ? (isLight ? "#FEF3C7" : "rgba(245, 158, 11, 0.2)") : "#10B981",
                            color: devotion.is_archived || filterActiveTab === "archived" ? (isLight ? "#B45309" : "#FBBF24") : "#FFFFFF",
                            border: devotion.is_archived || filterActiveTab === "archived" ? (isLight ? "1px solid #FDE68A" : "1px solid rgba(245, 158, 11, 0.4)") : "none",
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Tooltip title="View Complete Devotion">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDevotionDetail(devotion)}
                              sx={{
                                color: isLight ? "#0F766E" : "#64D2E2",
                                bgcolor: isLight ? "rgba(15, 118, 110, 0.08)" : "rgba(76, 161, 175, 0.15)",
                                '&:hover': { bgcolor: isLight ? "rgba(15, 118, 110, 0.18)" : "rgba(76, 161, 175, 0.3)" }
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {filterActiveTab === "archived" ? (
                            <>
                              <Tooltip title="Restore Devotion">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRestore(devotion.id)}
                                  sx={{
                                    color: "#10B981",
                                    bgcolor: "rgba(16, 185, 129, 0.1)",
                                    '&:hover': { bgcolor: "rgba(16, 185, 129, 0.2)" }
                                  }}
                                >
                                  <UnarchiveIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permanently Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePermanentDelete(devotion.id)}
                                  sx={{
                                    color: "#EF4444",
                                    bgcolor: "rgba(239, 68, 68, 0.1)",
                                    '&:hover': { bgcolor: "rgba(239, 68, 68, 0.2)" }
                                  }}
                                >
                                  <DeleteForeverIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              {canModifyDevotion(devotion) && (
                                <Tooltip title="Edit Devotion">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(devotion.id)}
                                    sx={{
                                      color: isLight ? "#0F766E" : "#64D2E2",
                                      bgcolor: isLight ? "rgba(15, 118, 110, 0.08)" : "rgba(76, 161, 175, 0.15)",
                                      '&:hover': { bgcolor: isLight ? "rgba(15, 118, 110, 0.18)" : "rgba(76, 161, 175, 0.3)" }
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Archive Devotion">
                                <IconButton
                                  size="small"
                                  onClick={() => handleArchive(devotion)}
                                  sx={{
                                    color: "#F59E0B",
                                    bgcolor: "rgba(245, 158, 11, 0.1)",
                                    '&:hover': { bgcolor: "rgba(245, 158, 11, 0.2)" }
                                  }}
                                >
                                  <ArchiveIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              {canModifyDevotion(devotion) && (
                                <Tooltip title="Delete Devotion">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(devotion.id)}
                                    sx={{
                                      color: "#EF4444",
                                      bgcolor: "rgba(239, 68, 68, 0.1)",
                                      '&:hover': { bgcolor: "rgba(239, 68, 68, 0.2)" }
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        )}

        {/* Pagination OUTSIDE the grid */}
        {effectiveTotalPages > 1 && (activeSourceTab !== 'All' || allViewMode !== 'cards' || filterActiveTab === 'archived') && (
          <Box display="flex" justifyContent="center" mt={4} width="100%">
            <Pagination
              count={effectiveTotalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  '&.Mui-selected': {
                    backgroundColor: '#177373',
                    color: '#FFFFFF',
                    fontWeight: 700,
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(76, 161, 175, 0.2)',
                  }
                }
              }}
            />
          </Box>
        )}
          </>
        )}

        {/* Daily Devotion Detail View Dialog */}
        <Dialog
          open={openDetailViewDialog}
          onClose={handleCloseDevotionDetail}
          maxWidth="md"
          fullWidth
          PaperProps={{
            className: 'dark-devotion-dialog',
            sx: {
              background: 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
              border: '1px solid rgba(76, 161, 175, 0.35)',
              borderRadius: '16px',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.5)',
              color: '#FFFFFF',
              maxHeight: '90vh',
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <MenuBookIcon sx={{ color: '#4CA1AF', fontSize: 28 }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#FFFFFF', lineHeight: 1.2 }}>
                  {selectedDevotionForView?.title || 'Daily Devotion'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Daily Devotion • Complete Entry & Reflection
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDevotionDetail} sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            {selectedDevotionForView && (
              <Box display="flex" flexDirection="column" gap={2.5}>
                {/* Optional Media Image Banner */}
                {selectedDevotionForView.image_url || selectedDevotionForView.image ? (
                  <Box
                    component="img"
                    src={selectedDevotionForView.image_url || selectedDevotionForView.image}
                    alt={selectedDevotionForView.title}
                    sx={{
                      width: '100%',
                      maxHeight: 280,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  />
                ) : null}

                {/* Metadata Pills */}
                <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                  <Chip
                    size="small"
                    label={getDevotionSection(selectedDevotionForView, defaultCategoryName)}
                    sx={{
                      backgroundColor: 'rgba(23, 115, 115, 0.35)',
                      color: '#64d2e2',
                      border: '1px solid rgba(76, 161, 175, 0.45)',
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    icon={<CalendarTodayIcon sx={{ '&&': { color: '#64d2e2', fontSize: 15 } }} />}
                    label={new Date(selectedDevotionForView.post_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    size="small"
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.06)', color: 'rgba(255, 255, 255, 0.85)' }}
                  />
                  {selectedDevotionForView.author && (
                    <Chip
                      icon={<PersonIcon sx={{ '&&': { color: '#4CA1AF', fontSize: 16 } }} />}
                      label={`Author: ${selectedDevotionForView.author}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.06)', color: 'rgba(255, 255, 255, 0.85)' }}
                    />
                  )}
                  {selectedDevotionForView.post_only_this_org ? (
                    <Chip
                      icon={<LockOutlinedIcon sx={{ '&&': { color: '#fbbf24', fontSize: 14 } }} />}
                      label="GPN Only"
                      size="small"
                      sx={{ bgcolor: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.35)', fontWeight: 600 }}
                    />
                  ) : (!selectedDevotionForView.org_id || Number(selectedDevotionForView.org_id) <= 1) ? (
                    <Chip
                      icon={<PublicIcon sx={{ '&&': { color: '#64d2e2', fontSize: 14 } }} />}
                      label="All Organizations"
                      size="small"
                      sx={{ bgcolor: 'rgba(76, 161, 175, 0.15)', color: '#64d2e2', border: '1px solid rgba(76, 161, 175, 0.35)', fontWeight: 600 }}
                    />
                  ) : null}
                  {selectedDevotionForView.video_url && (
                    <Chip
                      icon={<OndemandVideoIcon sx={{ '&&': { color: '#64d2e2', fontSize: 15 } }} />}
                      label="Watch Video"
                      component="a"
                      href={selectedDevotionForView.video_url}
                      target="_blank"
                      rel="noreferrer"
                      clickable
                      sx={{
                        bgcolor: 'rgba(76, 161, 175, 0.2)',
                        color: '#64d2e2',
                        border: '1px solid rgba(76, 161, 175, 0.4)',
                        fontWeight: 600,
                        '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.35)' }
                      }}
                    />
                  )}
                </Box>

                {/* Scripture / Quote Card */}
                {selectedDevotionForView.quote && stripHtml(selectedDevotionForView.quote).trim() && (
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(76, 161, 175, 0.08)',
                      borderLeft: '4px solid #4CA1AF',
                      border: '1px solid rgba(76, 161, 175, 0.2)',
                      borderLeftWidth: '4px',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <FormatQuoteIcon sx={{ color: '#4CA1AF', fontSize: 22 }} />
                      <Typography variant="subtitle2" sx={{ color: '#64d2e2', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Scripture & Quote of the Day
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#FFFFFF',
                        fontStyle: 'italic',
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        pl: 0.5,
                      }}
                    >
                      "{stripHtml(selectedDevotionForView.quote)}"
                    </Typography>
                  </Box>
                )}

                {/* Devotion Content */}
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2.5,
                    bgcolor: 'rgba(255, 255, 255, 0.025)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#64d2e2', fontWeight: 600, mb: 1.5, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Full Devotion Message
                  </Typography>
                  <Box
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.92rem',
                      lineHeight: 1.8,
                      '& p': { mb: 1.5 },
                      '& a': { color: '#64d2e2', textDecoration: 'underline' },
                      '& h1, & h2, & h3, & h4': { color: '#FFFFFF', mt: 2, mb: 1 },
                      '& ul, & ol': { pl: 3, mb: 1.5 },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedDevotionForView.post_content || '')
                    }}
                  />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
              {selectedDevotionForView && filterActiveTab !== 'archived' && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    const id = selectedDevotionForView.id;
                    handleCloseDevotionDetail();
                    handleEdit(id);
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '10px',
                    px: 2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #208e8e 0%, #2f8391 100%)',
                    }
                  }}
                >
                  Edit Devotion
                </Button>
              )}

              {/* Archive button */}
              {selectedDevotionForView && filterActiveTab !== 'archived' && !archivedList.some(a => String(a.id) === String(selectedDevotionForView.id)) ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleArchive(selectedDevotionForView)}
                  startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: "#ffb74d",
                    borderColor: "rgba(255, 183, 77, 0.6)",
                    bgcolor: "rgba(255, 183, 77, 0.12)",
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    px: 1.8,
                    "&:hover": { borderColor: "#ffb74d", bgcolor: "rgba(255, 183, 77, 0.25)", color: "#FFFFFF" }
                  }}
                >
                  Archive
                </Button>
              ) : selectedDevotionForView ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      handleRestore(selectedDevotionForView.id);
                      handleCloseDevotionDetail();
                    }}
                    startIcon={<UnarchiveIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: "#4CAF50",
                      borderColor: "rgba(76, 175, 80, 0.6)",
                      bgcolor: "rgba(76, 175, 80, 0.12)",
                      textTransform: "none",
                      borderRadius: "10px",
                      fontWeight: 700,
                      px: 1.8,
                      "&:hover": { borderColor: "#4CAF50", bgcolor: "rgba(76, 175, 80, 0.25)", color: "#FFFFFF" }
                    }}
                  >
                    Restore
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handlePermanentDelete(selectedDevotionForView.id)}
                    startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: "#EF4444",
                      borderColor: "rgba(239, 68, 68, 0.6)",
                      bgcolor: "rgba(239, 68, 68, 0.12)",
                      textTransform: "none",
                      borderRadius: "10px",
                      fontWeight: 700,
                      px: 1.8,
                      "&:hover": { borderColor: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.25)", color: "#FFFFFF" }
                    }}
                  >
                    Delete
                  </Button>
                </>
              ) : null}

              {/* Direct Delete button for active devotion */}
              {selectedDevotionForView && filterActiveTab !== 'archived' && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleDelete(selectedDevotionForView.id)}
                  startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: "#EF4444",
                    borderColor: "rgba(239, 68, 68, 0.6)",
                    bgcolor: "rgba(239, 68, 68, 0.12)",
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    px: 1.8,
                    "&:hover": { borderColor: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.25)", color: "#FFFFFF" }
                  }}
                >
                  Delete
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<TimelineIcon />}
                onClick={() => setUserActivityModalOpen(true)}
                sx={{
                  color: "#64d2e2",
                  borderColor: "rgba(76, 161, 175, 0.6)",
                  bgcolor: "rgba(76, 161, 175, 0.12)",
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 600,
                  px: 2,
                  "&:hover": { borderColor: "#64d2e2", bgcolor: "rgba(76, 161, 175, 0.25)", color: "#FFFFFF" }
                }}
              >
                View User Activity
              </Button>
              <Button
                variant="outlined"
                startIcon={<CalendarTodayIcon />}
                onClick={() => setUserProgressModalOpen(true)}
                sx={{
                  color: "#ffb74d",
                  borderColor: "rgba(255, 183, 77, 0.6)",
                  bgcolor: "rgba(255, 183, 77, 0.12)",
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 600,
                  px: 2,
                  "&:hover": { borderColor: "#ffb74d", bgcolor: "rgba(255, 183, 77, 0.25)", color: "#FFFFFF" }
                }}
              >
                Subscriber Progress (Floating)
              </Button>
            </Box>

            <Button
              onClick={handleCloseDevotionDetail}
              variant="outlined"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                px: 2.5,
                '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Progress Dialog for Floating Schedule */}
        <Dialog
          open={userProgressModalOpen}
          onClose={() => setUserProgressModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: 'linear-gradient(145deg, #15222E 0%, #1A2E3B 100%)',
              color: '#FFFFFF',
              border: '1px solid rgba(76, 161, 175, 0.35)',
              boxShadow: '0 16px 40px rgba(0,0,0,0.5)'
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarTodayIcon sx={{ color: '#ffb74d', fontSize: 26 }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Individual Subscriber Progress (Floating Schedule)
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Each believer starts on their own start date • 15 Days Devotion
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setUserProgressModalOpen(false)} size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 2.5 }}>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#14222B' }}>
                    <TableCell sx={{ color: '#E2E8F0', fontWeight: 700 }}>Believer</TableCell>
                    <TableCell sx={{ color: '#E2E8F0', fontWeight: 700 }}>Start Date</TableCell>
                    <TableCell sx={{ color: '#E2E8F0', fontWeight: 700 }}>Current Day</TableCell>
                    <TableCell sx={{ color: '#E2E8F0', fontWeight: 700 }}>Est. End Date</TableCell>
                    <TableCell sx={{ color: '#E2E8F0', fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { name: 'Grace Mathew', email: 'grace.m@glocalprayer.net', start: '2026-10-13', currentDay: 4, totalDays: 15, end: '2026-10-27', status: 'Active (On Track)' },
                    { name: 'David Samuel', email: 'david.s@glocalprayer.net', start: '2026-10-16', currentDay: 1, totalDays: 15, end: '2026-10-30', status: 'Day 1 Started' },
                    { name: 'Hannah Joseph', email: 'hannah.j@glocalprayer.net', start: '2026-10-10', currentDay: 7, totalDays: 15, end: '2026-10-24', status: 'Active (On Track)' },
                    { name: 'Timothy Paul', email: 'timothy.p@glocalprayer.net', start: '2026-10-01', currentDay: 14, totalDays: 15, end: '2026-10-15', status: 'Finishing Soon' }
                  ].map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ color: '#FFFFFF' }}>
                        <Box display="flex" alignItems="center" gap={1.2}>
                          <Avatar sx={{ width: 30, height: 30, bgcolor: '#177373', fontSize: 13, fontWeight: 700 }}>
                            {row.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{row.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{row.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>{row.start}</TableCell>
                      <TableCell sx={{ color: '#4CA1AF', fontWeight: 700 }}>
                        Day {row.currentDay} of {row.totalDays}
                      </TableCell>
                      <TableCell sx={{ color: '#FFFFFF', fontSize: '0.85rem' }}>{row.end}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: 'rgba(76, 175, 80, 0.15)',
                            color: '#81C784',
                            border: '1px solid rgba(76, 175, 80, 0.3)'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Button
              onClick={() => setUserProgressModalOpen(false)}
              variant="contained"
              sx={{ borderRadius: 2, bgcolor: '#177373', textTransform: 'none' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* View User Activity Modal for this Daily Devotion */}
        <ViewUserActivityModal
          open={userActivityModalOpen}
          onClose={() => setUserActivityModalOpen(false)}
          contentType="devotion"
          contentId={selectedDevotionForView?.id}
          contentTitle={selectedDevotionForView?.title}
          item={selectedDevotionForView}
        />

        {/* Edit Devotion Dialog */}
        {editUser && (
          <Dialog
            open={!!editUser}
            onClose={() => setEditUser(null)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              className: 'dark-devotion-dialog',
              sx: {
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 161, 175, 0.3)',
                borderRadius: { xs: 2, sm: 3 },
                m: { xs: 1, sm: 2 },
                width: { xs: 'calc(100% - 16px)', sm: '100%' },
                maxHeight: '92vh',
                color: '#FFFFFF'
              }
            }}
          >
            <DialogTitle className="dark-dialog-title" sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2, color: '#FFFFFF !important', fontWeight: 700 }}>
              Edit Daily Devotion
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
                {/* Row 1: Post Date * & Devotion Name */}
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white', mb: 0.5 }}>
                      Post Date *
                    </Typography>
                    <TextField
                      fullWidth
                      type="date"
                      value={editUser.post_date}
                      onChange={(e) =>
                        setEditUser({
                          ...editUser,
                          post_date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          color: '#FFFFFF',
                        }
                      }}
                    />
                  </Box>

                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white' }}>
                        Devotion Name
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setEditIsNewCategory(!editIsNewCategory)}
                        sx={{ color: '#64d2e2', textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0, fontWeight: 600 }}
                      >
                        {editIsNewCategory ? "Choose Existing" : "+ New Devotion Name"}
                      </Button>
                    </Box>
                    {editIsNewCategory ? (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`e.g. ${defaultCategoryName || 'Kumara Krupa'}, Youth Ministry...`}
                        value={editUser.category || ''}
                        onChange={(e) => setEditUser({ ...editUser, category: e.target.value })}
                        helperText="Enter new devotion name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            color: '#FFFFFF',
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#64d2e2',
                            fontSize: '0.75rem',
                          }
                        }}
                      />
                    ) : (
                      <FormControl fullWidth size="small">
                        <Select
                          value={editUser.category || (getDevotionSection(editUser) || defaultCategoryName || 'Kumara Krupa')}
                          onChange={(e) => {
                            if (e.target.value === '__add_new__') {
                              setEditIsNewCategory(true);
                              setEditUser({ ...editUser, category: '' });
                            } else {
                              setEditUser({ ...editUser, category: e.target.value });
                            }
                          }}
                          sx={{
                            borderRadius: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.04)',
                            color: '#FFFFFF',
                            '& .MuiSvgIcon-root': { color: '#64d2e2' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' }
                          }}
                        >
                          {sourceTabs.filter(t => t !== 'All').map((tabName) => (
                            <MenuItem key={tabName} value={tabName} sx={{ color: '#fff', bgcolor: '#1a2636' }}>
                              {tabName}
                            </MenuItem>
                          ))}
                          <MenuItem value="__add_new__" sx={{ color: '#64d2e2', bgcolor: '#1a2636', fontWeight: 600 }}>
                            + Add New Devotion Name...
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Box>

                {/* Row 2: Title * & Author */}
                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important', mb: 0.5 }}>
                      Title *
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={editUser.title}
                      onChange={(e) => setEditUser({ ...editUser, title: e.target.value })}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          color: '#FFFFFF',
                        }
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white', mb: 0.5 }}>
                      Author
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. Pastor John Doe"
                      value={editUser.author || ''}
                      onChange={(e) => setEditUser({ ...editUser, author: e.target.value })}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          color: '#FFFFFF',
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Row 3: Video URL (Optional) */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important' }}>
                      Video URL (Optional)
                    </Typography>
                    <Tooltip title="Optional: Add a YouTube URL (or MP4/video link). Mobile users will be able to watch this video in the app. If both image and video are added, mobile users can swipe between them.">
                      <HelpOutlineIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. https://www.youtube.com/watch?v=... or https://youtu.be/..."
                    value={editUser.video_url || ''}
                    onChange={(e) => setEditUser({ ...editUser, video_url: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <OndemandVideoIcon sx={{ color: '#64d2e2', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Plays inline or popup on mobile. If both image and video are added, users can swipe between them."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        color: '#FFFFFF',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '0.72rem',
                        mt: 0.25
                      }
                    }}
                  />
                </Box>

                {/* Organization Scope (Super Admin in GPN) */}
                {(isSuperAdminInGPN || !editUser?.org_id || Number(editUser?.org_id) <= 1) && (
                  <Box
                    sx={{
                      p: 1.5,
                      px: 2,
                      borderRadius: 2,
                      background: editPostOnlyThisOrg
                        ? 'rgba(23, 115, 115, 0.2)'
                        : 'rgba(255, 255, 255, 0.03)',
                      border: editPostOnlyThisOrg
                        ? '1px solid rgba(76, 161, 175, 0.5)'
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editPostOnlyThisOrg}
                          onChange={(e) => setEditPostOnlyThisOrg(e.target.checked)}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            '&.Mui-checked': {
                              color: '#64d2e2',
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                              Post only in this organization
                            </Typography>
                            <HelpTooltip
                              title="Organization Scope"
                              purpose="Control whether this daily devotion is shared globally or restricted to Glocal Prayer Network."
                              howItWorks="By default, Super Admin devotions are shared across all organizations in the network. Check this option if you want to publish it strictly for the Glocal Prayer Network organization."
                              mobileEffect={editPostOnlyThisOrg ? "Visible only to users belonging to Glocal Prayer Network." : "Visible to all believers across every connected organization."}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: editPostOnlyThisOrg ? '#64d2e2' : 'rgba(255, 255, 255, 0.55)', display: 'block', mt: 0.25 }}>
                            {editPostOnlyThisOrg
                              ? "Devotion is only published in Glocal Prayer Network (not shared with other organizations)."
                              : "Default: This devotion is shared with all organizations across the Glocal Prayer Network."}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}

                {/* Image Upload (Edit Dialog) */}
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white' }}>Image (Optional)</Typography>
                      <Tooltip title="Upload an image to display inside the devotion card on mobile. Recommended size: 16:9 ratio (1280 × 720 px or 1080 × 600 px).">
                        <HelpOutlineIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 600, bgcolor: 'rgba(100, 210, 226, 0.1)', px: 1, py: 0.25, borderRadius: 1 }}>
                      📐 Recommended: 16:9 ratio (1280 × 720 px)
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      border: '2px dashed rgba(76,161,175,0.4)',
                      borderRadius: '8px',
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: '#4CA1AF', backgroundColor: 'rgba(76,161,175,0.07)' },
                    }}
                    onClick={() => document.getElementById('edit-devotion-image-upload').click()}
                  >
                    <input
                      id="edit-devotion-image-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setEditImageFile(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setEditImagePreview(reader.result);
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    {editImagePreview || editUser.image_url ? (
                      <Box>
                        <img src={editImagePreview || editUser.image_url} alt="preview" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'cover' }} />
                        <Typography variant="caption" sx={{ color: '#64d2e2', display: 'block', mt: 1 }}>
                          {editImageFile ? `${editImageFile.name} — New image selected` : 'Current image — Click to replace'}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Box component="span" sx={{ fontSize: 36, display: 'block', mb: 1 }}>🖼️</Box>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                          Click to upload an image
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64d2e2', display: 'block', mt: 0.5, fontWeight: 600 }}>
                          📱 Recommended size: 16:9 ratio (1280 × 720 px or 1080 × 600 px) for optimal fit on mobile screens
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 0.25 }}>
                          JPG, PNG, GIF, WEBP • Max 5MB
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box sx={{
                  '& .ql-toolbar': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px 8px 0 0',
                  },
                  '& .ql-container': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '0 0 8px 8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    color: '#FFFFFF',
                    minHeight: { xs: 120, sm: 160 },
                  }
                }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white', mb: 0.5 }}>
                    Daily Devotion Content *
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={editUser.post_content}
                    onChange={(content) =>
                      setEditUser((prev) => ({ ...prev, post_content: content }))
                    }
                  />
                </Box>

                <Box sx={{
                  '& .ql-toolbar': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px 8px 0 0',
                  },
                  '& .ql-container': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '0 0 8px 8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    color: '#FFFFFF',
                    minHeight: { xs: 80, sm: 100 },
                  }
                }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'white', mb: 0.5 }}>
                    Quote of the Day
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={editUser.quote}
                    onChange={(content) =>
                      setEditUser((prev) => ({ ...prev, quote: content }))
                    }
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Button onClick={handleViewDialogClose} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                sx={{
                  background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Add Devotion Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            className: 'dark-devotion-dialog',
            sx: {
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(76, 161, 175, 0.3)',
              borderRadius: { xs: 2, sm: 3 },
              m: { xs: 1, sm: 2 },
              width: { xs: 'calc(100% - 16px)', sm: '100%' },
              maxHeight: '92vh',
              color: '#FFFFFF'
            }
          }}
        >
          <DialogTitle className="dark-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', color: '#FFFFFF !important' }}>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#FFFFFF !important', fontSize: { xs: '1.1rem', sm: '1.35rem' } }} style={{ color: '#FFFFFF' }}>
              Add Daily Devotion
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleManualSaveDraft}
                sx={{
                  color: '#64d2e2',
                  borderColor: 'rgba(76, 161, 175, 0.4)',
                  bgcolor: 'rgba(76, 161, 175, 0.1)',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.25)',
                    borderColor: '#4CA1AF',
                  },
                }}
              >
                Save Draft
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DraftsIcon sx={{ fontSize: 16 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('daily-devotion'));
                  setDraftsOpen(true);
                }}
                sx={{
                  bgcolor: 'rgba(76, 161, 175, 0.3)',
                  border: '1px solid rgba(76, 161, 175, 0.6)',
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(76, 161, 175, 0.5)',
                  },
                }}
              >
                Drafts ({draftsList.length}/5)
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
              {/* Row 1: Post Date * & Devotion Name in one line */}
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <CalendarTodayIcon sx={{ color: '#4CA1AF', fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important' }}>
                      Post Date *
                    </Typography>
                    <HelpTooltip
                      title="Devotion Publication Date"
                      purpose="Date when this devotional message will be shown to mobile users."
                      howItWorks="Select the day believers should read and meditate on this word."
                      mobileEffect="Users opening the app on this date will see this as their message for the day."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={PostDate}
                    onChange={(e) => setPostDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        color: '#FFFFFF',
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important' }}>
                        Devotion Name {isNewDevotionMode && '*'}
                      </Typography>
                      <HelpTooltip
                        title="Devotion Name"
                        purpose="Defines the devotion name/tab where this devotion appears."
                        howItWorks={isNewDevotionMode
                          ? "Type a new devotion name to create a new category/tab."
                          : "Choose an existing devotion or click to add a new one."}
                        mobileEffect="Shown as devotion tabs at the top for easy filtering."
                      />
                    </Box>
                    <Button
                      size="small"
                      onClick={() => {
                        setIsNewDevotionMode(!isNewDevotionMode);
                        if (!isNewDevotionMode) {
                          setCategory('');
                        } else {
                          setCategory(activeSourceTab !== 'All' ? activeSourceTab : (defaultCategoryName || 'Kumara Krupa'));
                        }
                      }}
                      sx={{
                        color: '#64d2e2',
                        textTransform: 'none',
                        fontSize: '0.72rem',
                        p: 0,
                        minWidth: 0,
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline', background: 'transparent' }
                      }}
                    >
                      {isNewDevotionMode ? "Choose Existing Devotion" : "+ Add New Daily Devotion"}
                    </Button>
                  </Box>

                  {isNewDevotionMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      autoFocus
                      placeholder={`e.g. ${defaultCategoryName || 'Kumara Krupa'}, Youth Ministry...`}
                      value={Category}
                      onChange={(e) => setCategory(e.target.value)}
                      helperText="Creating a new devotion name/category"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          color: '#FFFFFF',
                          borderColor: '#4CA1AF',
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#64d2e2',
                          fontSize: '0.72rem',
                          mt: 0.25
                        }
                      }}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <Select
                        value={Category || (activeSourceTab !== 'All' ? activeSourceTab : (defaultCategoryName || 'Kumara Krupa'))}
                        onChange={(e) => {
                          if (e.target.value === '__add_new__') {
                            setIsNewDevotionMode(true);
                            setCategory('');
                          } else {
                            setCategory(e.target.value);
                          }
                        }}
                        sx={{
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          color: '#FFFFFF',
                          '& .MuiSvgIcon-root': { color: '#64d2e2' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' }
                        }}
                      >
                        {sourceTabs.filter(t => t !== 'All').map((tabName) => (
                          <MenuItem key={tabName} value={tabName} sx={{ color: '#fff', bgcolor: '#1a2636', '&:hover': { bgcolor: '#243447' } }}>
                            {tabName}
                          </MenuItem>
                        ))}
                        <MenuItem value="__add_new__" sx={{ color: '#64d2e2', bgcolor: '#1a2636', fontWeight: 600, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                          + Add New Daily Devotion...
                        </MenuItem>
                      </Select>
                      <FormHelperText sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.72rem', mt: 0.3 }}>
                        Adding under devotion: <span style={{ color: '#64d2e2', fontWeight: 700 }}>{Category || (activeSourceTab !== 'All' ? activeSourceTab : (defaultCategoryName || 'Kumara Krupa'))}</span>
                      </FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Box>

              {/* Row 2: Title * & Author in one line */}
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important' }}>
                      Title *
                    </Typography>
                    <HelpTooltip
                      title="Devotion Title"
                      purpose="Headline and subject of the devotion."
                      howItWorks="Enter a clear, inspiring title for today's meditation."
                      mobileEffect="Displayed prominently as the header on user home and devotion screens."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Walking in Grace and Truth"
                    value={Title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        color: '#FFFFFF',
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important' }}>
                      Author
                    </Typography>
                    <HelpTooltip
                      title="Author Attribution"
                      purpose="Credits the pastor, minister, or author of the devotional message."
                      howItWorks="Enter the author's full name or ministry title."
                      mobileEffect="Displayed below the devotion title on mobile devices."
                    />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Pastor John Doe"
                    value={Author}
                    onChange={(e) => setAuthor(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        color: '#FFFFFF',
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Row 3: Video URL (Optional) */}
              <Box>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600} className="field-label" sx={{ color: '#FFFFFF !important' }}>
                    Video URL (Optional)
                  </Typography>
                  <HelpTooltip
                    title="Devotional Video Link"
                    purpose="Optionally attach a video sermon, meditation, or praise video."
                    howItWorks="Paste any YouTube URL (e.g., https://youtu.be/... or https://www.youtube.com/watch?v=...) or MP4 video URL."
                    mobileEffect="Mobile users will be able to watch this video directly in the app. If both image and video are added, mobile users can swipe between them."
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  value={VideoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <OndemandVideoIcon sx={{ color: '#64d2e2', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Plays inline or popup on mobile. If both image and video are added, users can swipe between them."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      color: '#FFFFFF',
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.72rem',
                      mt: 0.25
                    }
                  }}
                />
              </Box>

              {/* Organization Scope (Super Admin in GPN) */}
              {isSuperAdminInGPN && (
                <Box
                  sx={{
                    p: 1.5,
                    px: 2,
                    borderRadius: 2,
                    background: postOnlyThisOrg
                      ? 'rgba(23, 115, 115, 0.2)'
                      : 'rgba(255, 255, 255, 0.03)',
                    border: postOnlyThisOrg
                      ? '1px solid rgba(76, 161, 175, 0.5)'
                      : '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.25s ease',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={postOnlyThisOrg}
                        onChange={(e) => setPostOnlyThisOrg(e.target.checked)}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.6)',
                          '&.Mui-checked': {
                            color: '#64d2e2',
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                            Post only in this organization
                          </Typography>
                          <HelpTooltip
                            title="Organization Scope"
                            purpose="Control whether this daily devotion is shared globally or restricted to Glocal Prayer Network."
                            howItWorks="By default, Super Admin devotions are shared across all organizations in the network. Check this option if you want to publish it strictly for the Glocal Prayer Network organization."
                            mobileEffect={postOnlyThisOrg ? "Visible only to users belonging to Glocal Prayer Network." : "Visible to all believers across every connected organization."}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: postOnlyThisOrg ? '#64d2e2' : 'rgba(255, 255, 255, 0.55)', display: 'block', mt: 0.25 }}>
                          {postOnlyThisOrg
                            ? "Only visible in Glocal Prayer Network (will not be shared with other organizations)."
                            : "Shared with all organizations across the Glocal Prayer Network by default."}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}

              {/* Image Upload (Add Dialog) */}
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#FFFFFF' }}>Image (Optional)</Typography>
                    <HelpTooltip
                      title="Devotion Image"
                      purpose="Upload an inspiring image to accompany the devotion."
                      howItWorks="Recommended size: 16:9 ratio (1280 × 720 px or 1080 × 600 px). Select a JPG, PNG, GIF, or WEBP file from your device. For a video, paste the YouTube URL in the Video URL field above."
                      mobileEffect="Displayed prominently at 16:9 aspect ratio at the top of the mobile devotion screen. If both image and video are added, mobile users can swipe between them."
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 600, bgcolor: 'rgba(100, 210, 226, 0.1)', px: 1, py: 0.25, borderRadius: 1 }}>
                    📐 Recommended: 16:9 ratio (1280 × 720 px)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: '2px dashed rgba(76,161,175,0.4)',
                    borderRadius: '8px',
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: '#4CA1AF', backgroundColor: 'rgba(76,161,175,0.07)' },
                  }}
                  onClick={() => document.getElementById('devotion-image-upload').click()}
                >
                  <input
                    id="devotion-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setImageFile(file);
                        const reader = new FileReader();
                        reader.onloadend = () => setImagePreview(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {imagePreview ? (
                    <Box>
                      <img src={imagePreview} alt="preview" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'cover' }} />
                      <Typography variant="caption" sx={{ color: '#64d2e2', display: 'block', mt: 1 }}>
                        {imageFile?.name} — Click to change
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box component="span" sx={{ fontSize: 36, display: 'block', mb: 1 }}>🖼️</Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                        Click to upload an image
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64d2e2', display: 'block', mt: 0.5, fontWeight: 600 }}>
                        📱 Recommended size: 16:9 ratio (1280 × 720 px or 1080 × 600 px) for optimal fit on mobile screens
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', display: 'block', mt: 0.25 }}>
                        JPG, PNG, GIF, WEBP • Max 5MB
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{
                '& .ql-toolbar': {
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  borderRadius: '8px 8px 0 0',
                },
                '& .ql-container': {
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '0 0 8px 8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  color: '#FFFFFF',
                  minHeight: { xs: 120, sm: 160 },
                }
              }}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#FFFFFF' }}>Daily Devotion Message *</Typography>
                  <HelpTooltip
                    title="Devotional Body"
                    purpose="The main scripture reflection, biblical message, and prayer points."
                    howItWorks="Use the rich editor to format text, highlight key verses, and organize points."
                    mobileEffect="Rendered as the primary article text for mobile app reading."
                  />
                </Box>
                <ReactQuill
                  theme="snow"
                  value={Dailydeovotion}
                  onChange={(content) => setDailydeovotion(content)}
                />
              </Box>

              <Box sx={{
                '& .ql-toolbar': {
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  borderRadius: '8px 8px 0 0',
                },
                '& .ql-container': {
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '0 0 8px 8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  color: '#FFFFFF',
                  minHeight: { xs: 80, sm: 100 },
                }
              }}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#FFFFFF' }}>Quote of the Day</Typography>
                  <HelpTooltip
                    title="Quote of the Day"
                    purpose="A key memory verse or pithy statement for quick sharing and reflection."
                    howItWorks="Enter a memorable one or two sentence quote."
                    mobileEffect="Displayed as an attractive highlight card that mobile users can share with one tap."
                  />
                </Box>
                <ReactQuill
                  theme="snow"
                  value={Quote}
                  onChange={(content) => setQuote(content)}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Button onClick={handleDialogClose} sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddDevotion}
              sx={{
                background: 'linear-gradient(135deg, #177373 0%, #256974 100%)',
                fontWeight: 600,
                textTransform: 'none',
                px: 3
              }}
            >
              Save Devotion
            </Button>
          </DialogActions>
        </Dialog>

        {/* Drafts Modal */}
        <DraftsModal
          open={draftsOpen}
          onClose={() => setDraftsOpen(false)}
          moduleName="Daily Devotion"
          drafts={draftsList}
          onContinueEditing={handleContinueDraft}
          onDeleteDraft={handleDeleteDraft}
        />

      </Box>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />

      {/* Inline Daily Devotion Settings Modal */}
      <DailyDevotionSettingsModal
        open={devotionSettingsModalOpen}
        onClose={() => setDevotionSettingsModalOpen(false)}
        organization={organization}
        onSettingsSaved={(newVal) => setDevotionType(newVal)}
      />

      {/* Mobile Daily Devotion Mockup & Example Modal */}
      <MobileDailyDevotionModal
        open={mobilePreviewModal.open}
        onClose={() => setMobilePreviewModal({ ...mobilePreviewModal, open: false })}
        isExample={mobilePreviewModal.isExample}
        devotion={mobilePreviewModal.devotion}
      />
    </>
  );
};

export default DailyDevotions;
